import { useEffect, useId, useRef, type ReactNode } from "react";
import { AccessibilityInfo, Platform, StyleSheet, Text, View, findNodeHandle, useWindowDimensions } from "react-native";

import { Modal } from "./Modal";
import { IconButton } from "./IconButton";
import type { UiTheme } from "../theme";

// Bottom action-sheet chrome over the shared <Modal> scrim: a rounded-top card
// pinned to the bottom, slide-in, tap-out dismiss. Encodes the sheet shell
// (radius, bottom safe-area padding, optional title + ✕ close row) ONCE for the
// hand-rolled bottom sheets (EffortScaleModal / SupersetMenuSheet /
// TimelineHistorySheet, next/consolidation.md). The body is caller-composed.
//
// Safe-area inset is injected as `bottomInset` (px) — @heraion/ui
// deliberately carries no react-native-safe-area-context dep, so the mount
// feeds useSafeAreaInsets().bottom (same seam as Maxes/EditMaxSheet). Web
// passes none.

type SheetBaseProps = {
  theme: UiTheme;
  visible: boolean;
  onRequestClose: () => void;
  /** Optional header title. */
  title?: string;
  /** Safe-area bottom inset (px) from the mount; 0 on web. */
  bottomInset?: number;
  /** Where the card is pinned. `"bottom"` (default) is the action sheet.
   *  `"end"` is a full-height side panel on the trailing edge, for a wide
   *  screen (INBOX-10, the bell's panel on web): same scrim, same tap-out,
   *  same header, only the placement differs. */
  edge?: "bottom" | "end";
  children: ReactNode;
};

/** RNW forwards tabIndex; RN's Text type does not know it. */
const WEB_FOCUSABLE = { tabIndex: -1 } as object;

/** Side-panel width cap (px), `edge="end"` only. */
const SIDE_PANEL_MAX = 420;

/**
 * The header ✕ close is all-or-nothing: passing `onClose` (which renders the
 * trailing ✕, usually === onRequestClose) REQUIRES an a11y label, so a
 * migration can never ship a mute close button. Omit both for sheets whose
 * dismiss is a full Cancel row in the body instead.
 */
type SheetCloseProps =
  | { onClose?: undefined; closeAccessibilityLabel?: undefined }
  | { onClose: () => void; closeAccessibilityLabel: string };

export type SheetProps = SheetBaseProps & SheetCloseProps;

export function Sheet({
  theme,
  visible,
  onRequestClose,
  title,
  onClose,
  closeAccessibilityLabel,
  bottomInset = 0,
  edge = "bottom",
  children,
}: SheetProps) {
  const styles = makeStyles(theme);
  const { width } = useWindowDimensions();
  const side = edge === "end";
  // Opening a sheet moves the screen reader (and the keyboard, on web) onto the
  // title; otherwise focus stays on the button behind the scrim. A sheet with no
  // title has nothing to land on and keeps the old behaviour.
  const titleRef = useRef<Text>(null);
  const titleId = useId();
  const hasTitle = title !== undefined;
  useEffect(() => {
    if (!visible || !hasTitle) return;
    // The Modal mounts its portal after this commit; wait one tick.
    const id = setTimeout(() => {
      const node = titleRef.current;
      if (!node) return;
      if (Platform.OS === "web") {
        (node as unknown as { focus?: (o: { preventScroll: boolean }) => void }).focus?.({
          preventScroll: true,
        });
      } else {
        const tag = findNodeHandle(node);
        if (tag != null) AccessibilityInfo.setAccessibilityFocus(tag);
      }
    }, 50);
    return () => clearTimeout(id);
  }, [visible, hasTitle]);
  return (
    <Modal
      theme={theme}
      visible={visible}
      onRequestClose={onRequestClose}
      animationType={side ? "fade" : "slide"}
      backdropStyle={side ? styles.backdropSide : styles.backdrop}
      cardSlotStyle={
        side ? [styles.sideSlot, { width: Math.min(SIDE_PANEL_MAX, width) }] : undefined
      }
    >
      <View
        role="dialog"
        aria-modal
        aria-labelledby={hasTitle ? titleId : undefined}
        style={[
          side ? styles.side : styles.sheet,
          { paddingBottom: theme.spacing.s5 + bottomInset },
        ]}
      >
        {title !== undefined || onClose ? (
          <View style={styles.header}>
            {/* No numberOfLines: a long title wraps, never cut. */}
            <Text
              ref={titleRef}
              nativeID={titleId}
              role="heading"
              {...WEB_FOCUSABLE}
              style={styles.title}
            >
              {title ?? ""}
            </Text>
            {onClose ? (
              // Shared IconButton (36px box + hitSlop → ≥44 touch, required
              // a11y label) instead of a hand-sized ✕ glyph.
              <IconButton
                theme={theme}
                name="close"
                accessibilityLabel={closeAccessibilityLabel ?? ""}
                onPress={onClose}
              />
            ) : null}
          </View>
        ) : null}
        {children}
      </View>
    </Modal>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    backdrop: { justifyContent: "flex-end" },
    backdropSide: { flexDirection: "row", justifyContent: "flex-end" },
    sideSlot: { height: "100%" },
    sheet: {
      backgroundColor: p.bg,
      borderTopLeftRadius: r.r4,
      borderTopRightRadius: r.r4,
      paddingHorizontal: s.s4,
      paddingTop: s.s4,
      gap: s.s3,
    },
    // Full height, square on the edge it touches, rounded where it meets
    // the scrim.
    side: {
      flex: 1,
      backgroundColor: p.bg,
      borderTopLeftRadius: r.r4,
      borderBottomLeftRadius: r.r4,
      paddingHorizontal: s.s4,
      paddingTop: s.s4,
      gap: s.s3,
    },
    header: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
    },
    title: { color: p.ink, fontSize: t.sizes.lg, fontWeight: "700", flexShrink: 1 },
  });
}
